// Fitting the sky to the gameplay area (docs/02-Game-Design/Sky & Viewport.md):
// the sky fills the area's height between the sky insets, s = (areaHeight −
// top − bottom) / 1000; whatever width that leaves over pans horizontally,
// a little past each end; a sky narrower than the view is centered. Pure
// functions, tested without a DOM.
import { SKY_HEIGHT } from '@wits/core';
import type { Platform } from '../components/usePlatform';

/** Room the screen keeps round the sky, px. */
export interface SkyInsets {
  /** Above the sky's top edge. */
  top: number;
  /** Past either end of a sky that pans. */
  side: number;
  /** Below the sky's bottom edge. */
  bottom: number;
}

export const NO_INSETS: SkyInsets = { top: 0, side: 0, bottom: 0 };

/**
 * The sky insets per platform (Aaron, 2026-10-30): half the top bar at the
 * top, about a pan button's width at either end, nothing at the bottom. The
 * star edge (65 units) only keeps a star inside the sky; keeping it clear of
 * the HUD is done here, in pixels, and by the HUD's own interactions.
 */
export const SKY_INSETS: Record<Platform, SkyInsets> = {
  desktop: { top: 26, side: 44, bottom: 0 },
  tablet: { top: 26, side: 44, bottom: 0 },
  phone: { top: 20, side: 44, bottom: 0 },
};

/** The device's safe areas, px (the notch, the home indicator). */
export interface SafeArea {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** A platform's insets, widened for the device's safe areas. */
export function skyInsets(platform: Platform, safe: SafeArea): SkyInsets {
  const base = SKY_INSETS[platform];
  return {
    top: base.top + safe.top,
    side: Math.max(base.side, safe.left, safe.right),
    bottom: base.bottom + safe.bottom,
  };
}

export interface Viewport {
  /** Pixels per sky unit. */
  scale: number;
  /** The visible width in sky units. */
  visibleWidth: number;
  /** The visible height in sky units: the sky and the insets above and below it. */
  visibleHeight: number;
  /** Sky units above the sky's top edge (the top inset). */
  offsetY: number;
  /** The smallest and largest pan (left edge of the view in sky units). */
  minPan: number;
  maxPan: number;
  pannable: boolean;
}

export function computeViewport(areaWidth: number, areaHeight: number, skyWidth: number, insets: SkyInsets = NO_INSETS): Viewport {
  const skyPx = areaHeight - insets.top - insets.bottom;
  const scale = skyPx > 0 ? skyPx / SKY_HEIGHT : 1;
  const visibleWidth = areaWidth / scale;
  const visibleHeight = areaHeight > 0 ? areaHeight / scale : SKY_HEIGHT;
  const offsetY = insets.top / scale;
  if (skyWidth <= visibleWidth) {
    // It fits: centered, with no pan, whatever the side insets.
    const centered = (skyWidth - visibleWidth) / 2;
    return { scale, visibleWidth, visibleHeight, offsetY, minPan: centered, maxPan: centered, pannable: false };
  }
  const side = insets.side / scale;
  return { scale, visibleWidth, visibleHeight, offsetY, minPan: 0 - side, maxPan: skyWidth - visibleWidth + side, pannable: true };
}

export function clampPan(pan: number, vp: Viewport): number {
  return Math.min(vp.maxPan, Math.max(vp.minPan, pan));
}

export function initialPan(vp: Viewport): number {
  return (vp.minPan + vp.maxPan) / 2;
}

export function viewBoxOf(pan: number, vp: Viewport): string {
  return `${pan} ${-vp.offsetY} ${vp.visibleWidth} ${vp.visibleHeight}`;
}

// How large a glyph is drawn is in the shared core (sky/glyph.ts), which the
// Worker's share page uses too.
export { GLYPH_DRAW_FRACTION, glyphScale, MIN_GLYPH_PX } from '@wits/core';

/** The tappable radius in sky units: never under 44 px (22 px radius) whatever the glyph draws at. */
export function hitRadius(scale: number, glyphRadius: number): number {
  return Math.max(glyphRadius, 22 / scale);
}

/**
 * How large the background stars draw on a screen, as a share of their
 * radius in the field: full size once the screen's short side reaches a
 * desktop window's height, smaller on smaller screens so a phone's sky is not
 * one of desktop-sized stars, never under STAR_SIZE_MIN.
 */
export const STAR_SIZE_REFERENCE_PX = 900;
export const STAR_SIZE_MIN = 0.55;

export function starSize(width: number, height: number): number {
  const short = Math.min(width, height);
  return Math.min(1, Math.max(STAR_SIZE_MIN, short / STAR_SIZE_REFERENCE_PX));
}
